import { QueryClient } from '@tanstack/react-query'

/**
 * React Query Client Configuration
 * Optimized for app with offline capabilities
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache Configuration
      staleTime: 1000 * 60 * 5,        // 5 minutes - data considered fresh
      gcTime: 1000 * 60 * 30,          // 30 minutes - keep in cache
      
      // Network & Retry Configuration
      retry: (failureCount, error) => {
        // Don't retry auth errors
        const status = (error as { status?: number })?.status
        if (status === 401 || status === 403) return false
        // Retry network errors up to 3 times
        return failureCount < 3
      },
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Background Refetch Configuration
      refetchOnWindowFocus: false,     // Disabled to prevent redundant signed URL generation
      refetchOnReconnect: true,        // Refetch when internet reconnects
      refetchOnMount: true,            // Refetch when component mounts
      
      // Offline Configuration
      networkMode: 'offlineFirst',     // Use cache when offline
    },
    mutations: {
      retry: (failureCount, error) => {
        // Don't retry auth errors or validation errors
        const status = (error as { status?: number })?.status
        if (status === 401 || status === 403 || status === 422) return false
        // Retry network errors up to 2 times for mutations
        return failureCount < 2
      },
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000),
      networkMode: 'online',           // Only run mutations when online
    },
  },
})

/**
 * Query Keys Factory
 * Centralized query key management for consistency
 */
export const queryKeys = {
  // Authentication
  auth: ['auth'] as const,
  currentUser: ['auth', 'current-user'] as const,
  
  // Projects
  projects: {
    all: ['projects'] as const,
    list: (filters?: Record<string, unknown>) => ['projects', 'list', filters] as const,
    detail: (id: string) => ['projects', 'detail', id] as const,
    current: ['projects', 'current'] as const,
    stats: (id: string) => ['projects', 'stats', id] as const,
  },
  
  // Analyses
  analyses: {
    all: ['analyses'] as const,
    list: (filters?: Record<string, unknown>) => ['analyses', 'list', filters] as const,
    detail: (id: string) => ['analyses', 'detail', id] as const,
    byProject: (projectId: string) => ['analyses', 'by-project', projectId] as const,
  },
  
  // Contractors
  contractors: {
    all: ['contractors'] as const,
    list: (filters?: Record<string, unknown>) => ['contractors', 'list', filters] as const,
    detail: (id: string) => ['contractors', 'detail', id] as const,
  },
  
  // Tickets
  tickets: {
    all: ['tickets'] as const,
    list: (filters?: Record<string, unknown>) => ['tickets', 'list', filters] as const,
    detail: (id: string) => ['tickets', 'detail', id] as const,
    byProject: (projectId: string) => ['tickets', 'by-project', projectId] as const,
  },
  
  // Email Templates
  emailTemplates: {
    all: ['email-templates'] as const,
    list: ['email-templates', 'list'] as const,
    detail: (id: string) => ['email-templates', 'detail', id] as const,
  },
  
  // Billing & Usage
  billing: {
    all: ['billing'] as const,
    subscription: ['billing', 'subscription'] as const,
  },

  // Admin
  admin: {
    all: ['admin'] as const,
    users: (filters?: Record<string, unknown>) => ['admin', 'users', filters] as const,
    stats: ['admin', 'stats'] as const,
  },

  // Defect Types
  defectTypes: {
    all: ['defect-types'] as const,
    list: (includeInactive?: boolean) => ['defect-types', 'list', { includeInactive }] as const,
    detail: (id: string) => ['defect-types', 'detail', id] as const,
    active: ['defect-types', 'active'] as const,
    agentDrafts: ['defect-types', 'agent-drafts'] as const,
    agentStatus: (id: string) => ['defect-types', 'agent-status', id] as const,
  },
} as const

/**
 * Cache Invalidation Helpers
 * Common patterns for invalidating related queries
 */
export const invalidateQueries = {
  // Invalidate all project-related data
  projects: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
  },

  // Invalidate analyses
  analyses: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.analyses.all })
  },

  // Invalidate tickets
  tickets: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all })
  },
  
  // Invalidate defect types
  defectTypes: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.defectTypes.all })
  },

  // Invalidate everything (use sparingly)
  all: () => {
    queryClient.invalidateQueries()
  },
}

/**
 * Optimistic Update Helpers
 * Common patterns for optimistic updates
 */
export const optimisticUpdates = {
  // Add item to list optimistically
  addToList: <T extends { id: string }>(
    queryKey: readonly unknown[],
    newItem: T,
    tempId?: string
  ) => {
    const itemWithId = tempId ? { ...newItem, id: tempId } : newItem
    queryClient.setQueryData(queryKey, (old: T[] | undefined) => {
      return old ? [...old, itemWithId] : [itemWithId]
    })
    return queryClient.getQueryData(queryKey)
  },
  
  // Update item in list optimistically
  updateInList: <T extends { id: string }>(
    queryKey: readonly unknown[],
    itemId: string,
    updates: Partial<T>
  ) => {
    queryClient.setQueryData(queryKey, (old: T[] | undefined) => {
      return old ? old.map(item => 
        item.id === itemId ? { ...item, ...updates } : item
      ) : []
    })
    return queryClient.getQueryData(queryKey)
  },
  
  // Remove item from list optimistically
  removeFromList: <T extends { id: string }>(
    queryKey: readonly unknown[],
    itemId: string
  ) => {
    queryClient.setQueryData(queryKey, (old: T[] | undefined) => {
      return old ? old.filter(item => item.id !== itemId) : []
    })
    return queryClient.getQueryData(queryKey)
  },
}